import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { ChevronLeft, ChevronRight, Pause, Play, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FALLBACK = [
  "https://images.unsplash.com/photo-1766290327644-a64c170325ae?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1606143412458-acc5f86de897?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1758962036723-952a3125d445?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1715285977619-6d9357168f46?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1616701318247-e87eb43e79e3?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.pexels.com/photos/9818653/pexels-photo-9818653.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

// Return a URL sized for the requested width. Handles internal /api/files URLs and external Unsplash/Pexels URLs.
function sizedUrl(src, width) {
  if (!src) return src;
  // Internal file proxy → use our resize endpoint
  if (src.startsWith("/api/files/")) {
    const sep = src.includes("?") ? "&" : "?";
    return `${BACKEND}${src}${sep}w=${width}`;
  }
  // Unsplash → ?w=
  if (src.includes("images.unsplash.com")) {
    const url = new URL(src);
    url.searchParams.set("w", String(width));
    url.searchParams.set("q", "82");
    return url.toString();
  }
  // Pexels → tune w/h params
  if (src.includes("images.pexels.com")) {
    const url = new URL(src);
    url.searchParams.set("w", String(width));
    url.searchParams.set("auto", "compress");
    url.searchParams.set("cs", "tinysrgb");
    return url.toString();
  }
  return src;
}

function buildSrcSet(src) {
  const widths = [480, 800, 1200, 1600];
  return widths.map((w) => `${sizedUrl(src, w)} ${w}w`).join(", ");
}

export default function Portfolio() {
  const { settings } = useSiteSettings();
  const images = useMemo(
    () => (settings?.portfolio_images?.length ? settings.portfolio_images : FALLBACK),
    [settings]
  );

  const featured = images.slice(0, Math.min(5, images.length));
  const gridImages = images;

  return (
    <div data-testid="portfolio-page">
      <FeaturedSlideshow images={featured} />
      <div className="container-x py-14 md:py-24">
        <div className="max-w-3xl mb-10 md:mb-14">
          <div className="eyebrow mb-3 md:mb-4">Selected Work</div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light leading-[1.05]">Portfolio</h1>
          <p className="text-muted-foreground mt-4 max-w-xl">A glimpse of recent sessions — from intimate maternity portraits to bold editorial work.</p>
        </div>
        {images.length === 0 ? (
          <div className="text-muted-foreground">No photos yet.</div>
        ) : (
          <MasonryGrid images={gridImages} />
        )}
      </div>
    </div>
  );
}

/* --------------- Featured slideshow with cross-fade + Ken Burns --------------- */
function FeaturedSlideshow({ images }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  // Preload next image for snappier transitions
  useEffect(() => {
    if (!images.length) return;
    const next = (idx + 1) % images.length;
    const img = new Image();
    img.src = sizedUrl(images[next], 1600);
  }, [idx, images]);

  // Auto-advance every 5s
  useEffect(() => {
    if (paused || images.length < 2) return;
    timerRef.current = setTimeout(() => setIdx((i) => (i + 1) % images.length), 5000);
    return () => clearTimeout(timerRef.current);
  }, [idx, paused, images.length]);

  if (!images.length) return null;

  return (
    <section
      className="relative w-full overflow-hidden bg-stone-900 aspect-[16/10] sm:aspect-[16/8] md:aspect-[16/7] lg:aspect-[21/9]"
      style={{ minHeight: 280, maxHeight: 620 }}
      data-testid="portfolio-slideshow"
    >
      {images.map((src, i) => {
        // Only mount the active + next slide. The remaining slides are
        // unmounted (no <img> tag, so the browser never requests them)
        // until the user scrolls to them — saves ~4 × hero image downloads.
        const isActive = i === idx;
        const isNext = i === (idx + 1) % images.length;
        const shouldMount = isActive || isNext;
        if (!shouldMount) {
          return (
            <div
              key={`${src}-${i}`}
              className="absolute inset-0 opacity-0 z-0 pointer-events-none"
              aria-hidden="true"
            />
          );
        }
        return (
        <div
          key={`${src}-${i}`}
          className={`absolute inset-0 transition-opacity duration-[1400ms] ease-out ${isActive ? "opacity-100 z-10" : "opacity-0 z-0"}`}
          aria-hidden={!isActive}
        >
          <img
            src={sizedUrl(src, 1600)}
            srcSet={buildSrcSet(src)}
            sizes="100vw"
            alt={`Featured ${i + 1}`}
            className={`w-full h-full object-cover ${isActive ? "kenburns" : ""}`}
            style={{ objectPosition: "center 20%" }}
            loading={i === 0 ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={i === 0 ? "high" : "low"}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50" />
        </div>
        );
      })}

      {/* Overlay text */}
      <div className="relative z-20 h-full flex flex-col items-center justify-center text-center text-white px-4 pointer-events-none">
        <div className="text-[10px] sm:text-xs tracking-[0.4em] uppercase opacity-90 mb-2 sm:mb-3">Allure Image · Portfolio</div>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light leading-[1] drop-shadow-lg">Moments<br /><em className="italic font-light opacity-90">made to last.</em></h1>
        <p className="mt-4 text-xs sm:text-sm max-w-xl opacity-90">An evolving collection of editorial portraits, maternity, family and brand work.</p>
      </div>

      {/* Controls */}
      {images.length > 1 && (
        <>
          <button
            onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)}
            className="hidden sm:flex absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 items-center justify-center bg-white/15 backdrop-blur-md text-white hover:bg-white/30 transition-colors"
            aria-label="Previous"
            data-testid="slideshow-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setIdx((i) => (i + 1) % images.length)}
            className="hidden sm:flex absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 items-center justify-center bg-white/15 backdrop-blur-md text-white hover:bg-white/30 transition-colors"
            aria-label="Next"
            data-testid="slideshow-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              {images.map((img, i) => (
                <button
                  key={img}
                  onClick={() => setIdx(i)}
                  aria-label={`Show slide ${i + 1}`}
                  className={`h-[2px] transition-all duration-300 ${i === idx ? "w-8 bg-white" : "w-4 bg-white/50 hover:bg-white/80"}`}
                  data-testid={`slideshow-dot-${i}`}
                />
              ))}
            </div>
            <button
              onClick={() => setPaused((p) => !p)}
              aria-label={paused ? "Play slideshow" : "Pause slideshow"}
              className="text-white/80 hover:text-white"
              data-testid="slideshow-toggle"
            >
              {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

/* --------------- Masonry grid with intersection-observer fade-up + lightbox --------------- */
function MasonryGrid({ images }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);

  return (
    <>
      <div
        className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-3 md:gap-4 [column-fill:_balance]"
        data-testid="portfolio-grid"
      >
        {images.map((src, i) => (
          <FadeInImage
            key={`${src}-${i}`}
            src={src}
            index={i}
            onClick={() => setLightboxIdx(i)}
          />
        ))}
      </div>

      {lightboxIdx !== null && (
        <Lightbox
          images={images}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onChange={setLightboxIdx}
        />
      )}
    </>
  );
}

function FadeInImage({ src, index, onClick }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        }
      },
      { rootMargin: "150px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Aspect variation for visual rhythm
  const aspectVariants = ["aspect-[4/5]", "aspect-[3/4]", "aspect-[4/5]", "aspect-[1/1]", "aspect-[3/5]"];
  const aspectClass = aspectVariants[index % aspectVariants.length];

  return (
    <button
      ref={ref}
      onClick={onClick}
      className={`group relative w-full mb-3 md:mb-4 break-inside-avoid overflow-hidden bg-muted block ${aspectClass} cursor-zoom-in`}
      data-testid={`portfolio-tile-${index}`}
      style={{ transitionDelay: `${(index % 12) * 30}ms` }}
    >
      {/* Skeleton shimmer until loaded */}
      <div
        className={`absolute inset-0 bg-gradient-to-br from-stone-200 via-stone-100 to-stone-200 ${loaded ? "opacity-0" : "opacity-100 animate-pulse"} transition-opacity duration-500`}
      />
      {visible && (
        <img
          src={sizedUrl(src, 800)}
          srcSet={buildSrcSet(src)}
          sizes="(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
          alt={`Portfolio ${index + 1}`}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-[900ms] ease-out
            ${loaded ? "opacity-100 scale-100" : "opacity-0 scale-[1.04]"}
            group-hover:scale-[1.05] group-hover:brightness-105`}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </button>
  );
}

/* --------------- Lightbox --------------- */
function Lightbox({ images, index, onClose, onChange }) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onChange((index - 1 + images.length) % images.length);
      else if (e.key === "ArrowRight") onChange((index + 1) % images.length);
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [index, images.length, onClose, onChange]);

  const src = images[index];

  return createPortal(
    <div
      className="fixed inset-0 bg-stone-950/95 z-[9999] flex items-center justify-center p-4 sm:p-10"
      onClick={onClose}
      data-testid="portfolio-lightbox"
      role="dialog"
      aria-modal="true"
    >
      {/* Floating close button — always visible top-right */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="fixed top-4 right-4 sm:top-6 sm:right-6 z-[110] w-12 h-12 rounded-full bg-white/95 hover:bg-white text-stone-900 flex items-center justify-center shadow-lg transition-colors"
        aria-label="Close"
        data-testid="lightbox-close"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Counter top-left */}
      <div className="fixed top-5 left-5 sm:top-7 sm:left-8 z-[110] text-white/80 text-xs tracking-wider pointer-events-none">
        {index + 1} / {images.length}
      </div>

      {/* Prev */}
      <button
        onClick={(e) => { e.stopPropagation(); onChange((index - 1 + images.length) % images.length); }}
        className="fixed left-2 sm:left-6 top-1/2 -translate-y-1/2 z-[110] w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/15 backdrop-blur-md text-white hover:bg-white/30 flex items-center justify-center"
        aria-label="Previous"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      {/* Next */}
      <button
        onClick={(e) => { e.stopPropagation(); onChange((index + 1) % images.length); }}
        className="fixed right-2 sm:right-6 top-1/2 -translate-y-1/2 z-[110] w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/15 backdrop-blur-md text-white hover:bg-white/30 flex items-center justify-center"
        aria-label="Next"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Image — stopPropagation so clicking image itself doesn't close */}
      <img
        src={sizedUrl(src, 1600)}
        srcSet={buildSrcSet(src)}
        sizes="100vw"
        alt={`Portfolio ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] max-w-[92vw] sm:max-w-[80vw] object-contain animate-fade-in"
      />

      {/* Hint */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[110] text-white/60 text-[10px] tracking-[0.2em] uppercase pointer-events-none">
        Tap outside · ESC to close
      </div>
    </div>,
    document.body
  );
}
